/* AdminSellerTrust — Seller Trust Score console (Feb 2026)
 *
 * Behavioral-only seller trust scoring. No buyer-input ratings, no
 * carrier signals, no external-factor signals. Score reflects only what
 * the seller controls.
 *
 *   • List view: every seller with composite score, tier, sample size
 *   • Drill-down: 6 sub-scores + raw metrics + excluded-signals panel
 *   • Manual override: engineering-scope only, mandatory reason, audited
 *
 * Phase 1: silent data collection — no buyer-facing surface yet.
 */
import { useEffect, useMemo, useState } from "react";
import {
  Search, Loader2, Shield, TrendingUp, AlertTriangle, RefreshCw,
  CheckCircle2, XCircle, ChevronRight, ArrowLeft, Gauge,
  Truck, MessageSquare, FileText, Award, Clock, History, Filter,
} from "lucide-react";
import api from "../../lib/api";
import { toast } from "sonner";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Textarea } from "../../components/ui/textarea";
import { Badge } from "../../components/ui/badge";

const TIER_META = {
  top_rated:         { label: "Top Rated",        bg: "bg-emerald-50",  text: "text-emerald-700",  ring: "ring-emerald-200",  dot: "bg-emerald-500" },
  trusted:           { label: "Trusted",          bg: "bg-sky-50",      text: "text-sky-700",      ring: "ring-sky-200",      dot: "bg-sky-500" },
  standard:          { label: "Standard",         bg: "bg-zinc-50",     text: "text-zinc-700",     ring: "ring-zinc-200",     dot: "bg-zinc-400" },
  probation:         { label: "Probation",        bg: "bg-amber-50",    text: "text-amber-700",    ring: "ring-amber-200",    dot: "bg-amber-500" },
  insufficient_data: { label: "Insufficient data", bg: "bg-stone-50",   text: "text-stone-500",    ring: "ring-stone-200",    dot: "bg-stone-300" },
};

const SUB_LABELS = {
  responsiveness:    { label: "Order responsiveness", weight: 30, icon: Clock,        hint: "Accept rate · time-to-accept · post-accept cancels" },
  fulfillment:       { label: "Fulfillment",          weight: 25, icon: Truck,        hint: "Handoff time · tracking · accuracy · OOS rate" },
  post_sale:         { label: "Post-sale support",    weight: 15, icon: RefreshCw,    hint: "Refund speed · packaging-attributed damage" },
  communication:     { label: "Communication",        weight: 10, icon: MessageSquare, hint: "Message response rate + speed" },
  invoice_integrity: { label: "Invoice integrity",    weight: 10, icon: FileText,     hint: "Invoice matches PO · no surprise charges" },
  compliance_tenure: { label: "Compliance & tenure",  weight: 10, icon: Shield,       hint: "KYB · W-9 · COI · policies · months on platform" },
};


function TierBadge({ tier, ...rest }) {
  const meta = TIER_META[tier] || TIER_META.insufficient_data;
  return (
    <span
      data-testid={`tier-badge-${tier}`}
      className={`inline-flex items-center gap-1.5 px-2 py-0.5 rounded-full text-[11px] font-medium ring-1 ${meta.bg} ${meta.text} ${meta.ring}`}
      {...rest}
    >
      <span className={`w-1.5 h-1.5 rounded-full ${meta.dot}`} />
      {meta.label}
    </span>
  );
}


function ScoreBar({ value, max = 100, color = "bg-foreground" }) {
  const pct = Math.max(0, Math.min(100, (value / max) * 100));
  return (
    <div className="w-full h-1.5 rounded-full bg-secondary overflow-hidden">
      <div className={`h-full ${color}`} style={{ width: `${pct}%` }} />
    </div>
  );
}


function SellerListView({ data, loading, search, setSearch, tierFilter, setTierFilter, onPick, onRollup, rollupBusy }) {
  const filtered = useMemo(() => {
    if (!data) return [];
    const q = search.trim().toLowerCase();
    return data.rows.filter(r => {
      if (tierFilter && r.tier !== tierFilter) return false;
      if (!q) return true;
      return (
        (r.seller_name || "").toLowerCase().includes(q) ||
        (r.seller_email || "").toLowerCase().includes(q)
      );
    });
  }, [data, search, tierFilter]);

  return (
    <div className="space-y-6" data-testid="seller-trust-list">
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div>
          <h1 className="display text-3xl tracking-tight">Seller Trust</h1>
          <p className="text-sm text-foreground/65 mt-1 max-w-2xl">
            Behavioral-only trust score for every seller. Reflects only seller-controlled signals —
            carrier delays, in-transit damage, and external factors are excluded.
            Methodology <span className="mono">{data?.methodology_version || "v1.0"}</span>.
          </p>
        </div>
        <Button
          onClick={onRollup}
          disabled={rollupBusy}
          variant="outline"
          className="rounded-full"
          data-testid="seller-trust-rollup-btn"
        >
          {rollupBusy ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <RefreshCw className="w-4 h-4 mr-2" />}
          Run rollup now
        </Button>
      </div>

      {/* Tier summary cards */}
      <div className="grid grid-cols-2 md:grid-cols-5 gap-2">
        {["top_rated", "trusted", "standard", "probation", "insufficient_data"].map(t => {
          const meta = TIER_META[t];
          const count = data?.buckets?.[t] ?? 0;
          const active = tierFilter === t;
          return (
            <button
              key={t}
              onClick={() => setTierFilter(active ? "" : t)}
              data-testid={`tier-filter-${t}`}
              className={`text-left rounded-xl border p-3 transition-all ${active ? "border-foreground bg-secondary/40" : "border-border hover:border-foreground/30"}`}
            >
              <div className="flex items-center gap-1.5">
                <span className={`w-1.5 h-1.5 rounded-full ${meta.dot}`} />
                <span className="text-[10px] mono uppercase tracking-wider text-foreground/55">{meta.label}</span>
              </div>
              <div className="display text-2xl mt-1">{count}</div>
            </button>
          );
        })}
      </div>

      {/* Search + filter chips */}
      <div className="flex items-center gap-2">
        <div className="relative flex-1 max-w-md">
          <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-foreground/40" />
          <Input
            value={search}
            onChange={e => setSearch(e.target.value)}
            placeholder="Search seller or email…"
            className="pl-9 rounded-full h-10"
            data-testid="seller-trust-search"
          />
        </div>
        {tierFilter && (
          <Button variant="outline" size="sm" onClick={() => setTierFilter("")} className="rounded-full text-xs gap-1">
            <Filter className="w-3 h-3" />
            {TIER_META[tierFilter]?.label} · clear
          </Button>
        )}
      </div>

      {/* Seller table */}
      <div className="rounded-xl border border-border overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-secondary/40 border-b border-border">
            <tr className="text-left text-[10px] mono uppercase tracking-wider text-foreground/55">
              <th className="px-4 py-2.5">Seller</th>
              <th className="px-4 py-2.5">Score</th>
              <th className="px-4 py-2.5">Tier</th>
              <th className="px-4 py-2.5 text-right">Sample size</th>
              <th className="px-4 py-2.5"></th>
            </tr>
          </thead>
          <tbody>
            {loading && (
              <tr><td colSpan={5} className="px-4 py-10 text-center text-foreground/55">
                <Loader2 className="w-5 h-5 animate-spin inline-block mr-2" />Loading…
              </td></tr>
            )}
            {!loading && filtered.length === 0 && (
              <tr><td colSpan={5} className="px-4 py-10 text-center text-foreground/55">
                No sellers match your filters. The nightly rollup populates this table — try
                "Run rollup now" if you just deployed.
              </td></tr>
            )}
            {!loading && filtered.map(r => (
              <tr
                key={r.seller_id}
                className="border-b border-border last:border-0 hover:bg-secondary/30 cursor-pointer transition-colors"
                onClick={() => onPick(r.seller_id)}
                data-testid={`seller-trust-row-${r.seller_id}`}
              >
                <td className="px-4 py-3">
                  <div className="font-medium">{r.seller_name}</div>
                  <div className="text-[11px] text-foreground/55 mono">{r.seller_email}</div>
                </td>
                <td className="px-4 py-3">
                  <div className="flex items-center gap-2">
                    <span className="display text-lg w-10">{r.composite_score?.toFixed(0) ?? "—"}</span>
                    <div className="w-24"><ScoreBar value={r.composite_score || 0} /></div>
                  </div>
                </td>
                <td className="px-4 py-3"><TierBadge tier={r.tier || "insufficient_data"} /></td>
                <td className="px-4 py-3 text-right text-foreground/70">{r.sample_size ?? 0}</td>
                <td className="px-4 py-3 text-right">
                  <ChevronRight className="w-4 h-4 text-foreground/40 inline-block" />
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}


function MetricRow({ label, value, suffix = "" }) {
  return (
    <div className="flex items-center justify-between py-1.5 text-sm border-b border-border last:border-0">
      <span className="text-foreground/70">{label}</span>
      <span className="font-medium mono">
        {value === null || value === undefined ? <span className="text-foreground/40">—</span> : `${value}${suffix}`}
      </span>
    </div>
  );
}


function SellerDrillView({ sellerId, onBack, onAfterOverride }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [overrideOpen, setOverrideOpen] = useState(false);
  const [delta, setDelta] = useState("");
  const [reason, setReason] = useState("");
  const [overrideBusy, setOverrideBusy] = useState(false);

  const fetchSeller = async () => {
    setLoading(true);
    try {
      const { data } = await api.get(`/admin/seller-trust/${sellerId}`);
      setData(data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not load seller trust profile.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchSeller(); /* eslint-disable-next-line */ }, [sellerId]);

  const applyOverride = async () => {
    const d = Number(delta);
    if (!Number.isFinite(d) || d === 0) {
      toast.error("Enter a non-zero delta between -100 and +100.");
      return;
    }
    if (reason.trim().length < 10) {
      toast.error("Reason must be at least 10 characters (audit requirement).");
      return;
    }
    setOverrideBusy(true);
    try {
      await api.post(`/admin/seller-trust/${sellerId}/override`, { delta: d, reason: reason.trim() });
      toast.success("Override applied and audited.");
      setOverrideOpen(false);
      setDelta(""); setReason("");
      await fetchSeller();
      onAfterOverride?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Override failed.");
    } finally {
      setOverrideBusy(false);
    }
  };

  if (loading || !data) {
    return (
      <div className="py-20 text-center text-foreground/55" data-testid="seller-trust-drill-loading">
        <Loader2 className="w-6 h-6 animate-spin inline-block mr-2" />Loading seller profile…
      </div>
    );
  }

  const { seller, score, history, overrides } = data;
  const m = score.metrics || {};
  const subs = score.sub_scores || {};

  return (
    <div className="space-y-6" data-testid="seller-trust-drill">
      <div className="flex items-center justify-between">
        <button onClick={onBack} className="inline-flex items-center gap-1.5 text-sm text-foreground/65 hover:text-foreground" data-testid="seller-trust-back">
          <ArrowLeft className="w-4 h-4" />Back to all sellers
        </button>
        <Button onClick={() => setOverrideOpen(true)} variant="outline" size="sm" className="rounded-full" data-testid="seller-trust-override-open">
          Manual override
        </Button>
      </div>

      {/* Header */}
      <div className="rounded-2xl border border-border p-6 bg-secondary/20">
        <div className="flex items-start justify-between gap-6">
          <div>
            <div className="text-[10px] mono uppercase tracking-[0.2em] text-foreground/55">Seller</div>
            <h2 className="display text-2xl mt-1">{seller.name}</h2>
            <div className="text-sm text-foreground/65 mt-1">{seller.email}</div>
          </div>
          <div className="text-right">
            <div className="display text-5xl tracking-tight" data-testid="seller-trust-composite">{score.composite_score?.toFixed(0) ?? "—"}</div>
            <div className="mt-2"><TierBadge tier={score.tier} /></div>
            <div className="text-[11px] text-foreground/55 mt-2">
              {score.sample_size} orders · methodology <span className="mono">{score.methodology_version}</span>
            </div>
          </div>
        </div>
      </div>

      {/* Sub-scores */}
      <div>
        <h3 className="display text-lg mb-3">Sub-scores</h3>
        <div className="grid md:grid-cols-2 gap-3">
          {Object.entries(SUB_LABELS).map(([key, meta]) => {
            const Icon = meta.icon;
            const v = subs[key] ?? 0;
            return (
              <div key={key} className="rounded-xl border border-border p-4" data-testid={`sub-${key}`}>
                <div className="flex items-center justify-between mb-2">
                  <div className="flex items-center gap-2">
                    <Icon className="w-4 h-4 text-foreground/65" />
                    <span className="text-sm font-medium">{meta.label}</span>
                    <Badge variant="outline" className="text-[10px] mono">{meta.weight}%</Badge>
                  </div>
                  <span className="display text-xl">{v.toFixed(0)}</span>
                </div>
                <ScoreBar value={v} />
                <p className="text-[11px] text-foreground/55 mt-2">{meta.hint}</p>
              </div>
            );
          })}
        </div>
      </div>

      {/* Raw metrics */}
      <div className="grid md:grid-cols-2 gap-6">
        <div className="rounded-xl border border-border p-4">
          <h4 className="display text-base mb-2">Order behavior</h4>
          <MetricRow label="Orders received (180d)" value={m.orders_received} />
          <MetricRow label="Orders accepted"        value={m.orders_accepted} />
          <MetricRow label="Orders shipped"         value={m.orders_shipped} />
          <MetricRow label="Accept rate"            value={m.accept_rate ? (m.accept_rate * 100).toFixed(1) : "—"} suffix="%" />
          <MetricRow label="Median time-to-accept"  value={m.time_to_accept_median_h} suffix=" h" />
          <MetricRow label="Post-accept cancel rate" value={m.post_cancel_rate ? (m.post_cancel_rate * 100).toFixed(2) : "—"} suffix="%" />
        </div>
        <div className="rounded-xl border border-border p-4">
          <h4 className="display text-base mb-2">Fulfillment behavior</h4>
          <MetricRow label="Median handoff time"     value={m.handoff_time_median_h} suffix=" h" />
          <MetricRow label="Tracking upload rate"    value={m.tracking_rate ? (m.tracking_rate * 100).toFixed(1) : "—"} suffix="%" />
          <MetricRow label="Order accuracy rate"     value={m.accuracy_rate ? (m.accuracy_rate * 100).toFixed(2) : "—"} suffix="%" />
          <MetricRow label="OOS cancel rate"         value={m.oos_cancel_rate ? (m.oos_cancel_rate * 100).toFixed(2) : "—"} suffix="%" />
          <MetricRow label="Packaging-damage rate"   value={m.packaging_damage_rate ? (m.packaging_damage_rate * 100).toFixed(2) : "—"} suffix="%" />
        </div>
        <div className="rounded-xl border border-border p-4">
          <h4 className="display text-base mb-2">Communication & support</h4>
          <MetricRow label="Threads (180d)"          value={m.messages_threads} />
          <MetricRow label="Message response rate"   value={m.msg_response_rate ? (m.msg_response_rate * 100).toFixed(1) : "—"} suffix="%" />
          <MetricRow label="Median response time"    value={m.msg_response_median_h} suffix=" h" />
          <MetricRow label="Median refund time"      value={m.refund_time_median_h} suffix=" h" />
        </div>
        <div className="rounded-xl border border-border p-4">
          <h4 className="display text-base mb-2">Invoice integrity & compliance</h4>
          <MetricRow label="Invoice match rate"      value={m.invoice_match_rate ? (m.invoice_match_rate * 100).toFixed(1) : "—"} suffix="%" />
          <MetricRow label="Surprise-charge rate"    value={m.surprise_charge_rate ? (m.surprise_charge_rate * 100).toFixed(2) : "—"} suffix="%" />
          <MetricRow label="Compliance score"        value={m.compliance_score ? (m.compliance_score * 100).toFixed(0) : "—"} suffix="%" />
          <MetricRow label="Tenure"                  value={m.tenure_months ? `${m.tenure_months.toFixed(0)}` : "—"} suffix=" months" />
        </div>
      </div>

      {/* Compliance breakdown */}
      {m.compliance_flags && (
        <div className="rounded-xl border border-border p-4">
          <h4 className="display text-base mb-3">Compliance checklist</h4>
          <div className="grid grid-cols-2 md:grid-cols-3 gap-2">
            {Object.entries(m.compliance_flags).map(([k, v]) => (
              <div key={k} className="flex items-center gap-2 text-sm">
                {v ? <CheckCircle2 className="w-4 h-4 text-emerald-600" /> : <XCircle className="w-4 h-4 text-stone-400" />}
                <span className={v ? "" : "text-foreground/50"}>{k.replace(/_/g, " ")}</span>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Excluded signals — transparency */}
      <div className="rounded-xl border border-amber-200 bg-amber-50/40 p-4">
        <h4 className="display text-base mb-2 flex items-center gap-2 text-amber-900">
          <AlertTriangle className="w-4 h-4" />Excluded from this score (by design)
        </h4>
        <p className="text-[12px] text-foreground/65 mb-2">
          These signals are out of the seller's control and would unfairly penalize them.
          They are tracked separately under carrier and buyer-fault metrics.
        </p>
        <ul className="space-y-1 text-sm text-foreground/75">
          {(m.excluded_signals || []).map((s, i) => (
            <li key={i} className="flex items-start gap-2">
              <span className="text-amber-600">•</span><span>{s}</span>
            </li>
          ))}
        </ul>
      </div>

      {/* History */}
      {history && history.length > 0 && (
        <div className="rounded-xl border border-border p-4">
          <h4 className="display text-base mb-3 flex items-center gap-2"><History className="w-4 h-4" />Score history (last 30 snapshots)</h4>
          <div className="grid grid-cols-5 md:grid-cols-10 gap-1.5">
            {history.slice().reverse().map((h, i) => (
              <div key={i} className="text-center" title={`${h.date} · ${h.composite_score}`}>
                <div className="display text-xs">{h.composite_score?.toFixed(0)}</div>
                <div className="text-[9px] mono text-foreground/55 mt-0.5">{(h.date || "").slice(5)}</div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Overrides */}
      {overrides && overrides.length > 0 && (
        <div className="rounded-xl border border-border p-4">
          <h4 className="display text-base mb-2">Manual overrides</h4>
          <table className="w-full text-sm">
            <thead className="text-left text-[10px] mono uppercase tracking-wider text-foreground/55">
              <tr><th className="py-1">When</th><th>Δ</th><th>Reason</th><th>By</th></tr>
            </thead>
            <tbody>
              {overrides.map((o, i) => (
                <tr key={i} className="border-t border-border">
                  <td className="py-1.5 mono text-[11px] text-foreground/65">{(o.created_at || "").slice(0, 16).replace("T", " ")}</td>
                  <td className="py-1.5 mono">{o.delta > 0 ? "+" : ""}{o.delta}</td>
                  <td className="py-1.5">{o.reason}</td>
                  <td className="py-1.5 text-foreground/65">{o.applied_by_email}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Override modal (inline) */}
      {overrideOpen && (
        <div className="fixed inset-0 bg-black/40 z-50 grid place-items-center p-4" onClick={() => setOverrideOpen(false)}>
          <div className="bg-background rounded-2xl border border-border p-6 max-w-md w-full" onClick={e => e.stopPropagation()}>
            <h3 className="display text-lg mb-2">Manual score override</h3>
            <p className="text-sm text-foreground/65 mb-4">
              Engineering-only. Stamps an immutable audit row in <span className="mono">seller_score_overrides</span>.
            </p>
            <label className="text-[11px] mono uppercase tracking-wider text-foreground/55">Delta (-100 to +100)</label>
            <Input
              type="number" value={delta} onChange={e => setDelta(e.target.value)}
              placeholder="-5"
              className="mt-1 rounded-lg"
              data-testid="seller-trust-override-delta"
            />
            <label className="text-[11px] mono uppercase tracking-wider text-foreground/55 mt-4 block">Reason (required · ≥ 10 chars)</label>
            <Textarea
              value={reason} onChange={e => setReason(e.target.value)}
              placeholder="e.g. Confirmed seller is recovering from a one-time warehouse fire — temporary credit."
              rows={3}
              className="mt-1 rounded-lg"
              data-testid="seller-trust-override-reason"
            />
            <div className="flex justify-end gap-2 mt-5">
              <Button variant="ghost" onClick={() => setOverrideOpen(false)}>Cancel</Button>
              <Button onClick={applyOverride} disabled={overrideBusy} data-testid="seller-trust-override-submit">
                {overrideBusy && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}Apply override
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}


export default function AdminSellerTrust() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [tierFilter, setTierFilter] = useState("");
  const [pickedSeller, setPickedSeller] = useState(null);
  const [rollupBusy, setRollupBusy] = useState(false);

  const fetchList = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/admin/seller-trust");
      setData(data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not load seller trust list.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchList(); }, []);

  const runRollup = async () => {
    setRollupBusy(true);
    try {
      const { data } = await api.post("/admin/seller-trust/rollup-now");
      toast.success(`Rolled up ${data.written} sellers in ${(new Date(data.finished_at) - new Date(data.started_at)) / 1000}s.`);
      await fetchList();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Rollup failed.");
    } finally {
      setRollupBusy(false);
    }
  };

  return (
    <div className="space-y-6 max-w-6xl" data-testid="admin-seller-trust">
      {pickedSeller ? (
        <SellerDrillView
          sellerId={pickedSeller}
          onBack={() => { setPickedSeller(null); fetchList(); }}
          onAfterOverride={fetchList}
        />
      ) : (
        <SellerListView
          data={data}
          loading={loading}
          search={search} setSearch={setSearch}
          tierFilter={tierFilter} setTierFilter={setTierFilter}
          onPick={setPickedSeller}
          onRollup={runRollup}
          rollupBusy={rollupBusy}
        />
      )}
    </div>
  );
}
